import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { X, MapPin, ArrowRight, ChevronRight, LayoutGrid, BarChart3 } from 'lucide-react';
import { fetchProperties, imageUrl } from '@/lib/sheetData';
import { pricePerSqFt, formatPerSqFt } from '@/lib/priceUtils';

// Compact comparison table shown inside the project modal when "Compare BHK" is on.
const CompareTable = ({ listings }) => {
  // Compute price-per-sqft once so we can highlight best value.
  const enriched = listings.map((l) => ({ ...l, _pps: pricePerSqFt(l) }));
  const validPps = enriched.map((e) => e._pps).filter((n) => Number.isFinite(n));
  const bestPps = validPps.length ? Math.min(...validPps) : null;

  return (
    <div className="overflow-x-auto -mx-6 px-6" data-testid="compare-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-background-tertiary text-text-muted text-xs uppercase tracking-wider">
            <th className="text-left py-3 pr-4 font-medium">Listing</th>
            <th className="text-left py-3 pr-4 font-medium">Config</th>
            <th className="text-left py-3 pr-4 font-medium">Size</th>
            <th className="text-left py-3 pr-4 font-medium">Price</th>
            <th className="text-left py-3 pr-4 font-medium">₹ / sq ft</th>
            <th className="text-left py-3 pr-4 font-medium">Status</th>
            <th className="text-right py-3 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {enriched.map((l, idx) => {
            const isBest = bestPps != null && l._pps === bestPps && Number.isFinite(l._pps);
            return (
              <tr
                key={l.id}
                onClick={() => window.open(`/property/${l.id}`, '_blank')}
                className="border-b border-background-tertiary/60 hover:bg-background-secondary/60 cursor-pointer transition-colors"
                data-testid={`compare-row-${idx}`}
              >
                <td className="py-4 pr-4">
                  <div className="font-heading text-base text-text-primary">{l.title}</div>
                  <div className="text-text-muted text-xs flex items-center gap-1 mt-0.5">
                    <MapPin size={10} /> {l.location}
                  </div>
                </td>
                <td className="py-4 pr-4 text-text-primary">{l.bedrooms || '—'}</td>
                <td className="py-4 pr-4 text-text-secondary">{l.area || l.size || '—'}</td>
                <td className="py-4 pr-4 font-heading text-brand-green whitespace-nowrap">
                  {l.price || 'On request'}
                </td>
                <td className="py-4 pr-4 whitespace-nowrap">
                  <span className={isBest ? 'text-brand-green font-medium' : 'text-text-secondary'}>
                    {formatPerSqFt(l._pps)}
                  </span>
                  {isBest && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-green/10 text-brand-green">
                      Best value
                    </span>
                  )}
                </td>
                <td className="py-4 pr-4">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      l.status === 'Available' ? 'bg-brand-green/10 text-brand-green' : 'bg-red-500/10 text-red-600'
                    }`}
                  >
                    {l.status}
                  </span>
                </td>
                <td className="py-4 text-right">
                  <span className="inline-flex items-center gap-1 text-brand-green text-xs">
                    Details <ArrowRight size={12} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[11px] text-text-muted mt-4">
        ₹ / sq ft is computed from the starting price ÷ super built-up size and is indicative only.
      </p>
    </div>
  );
};

export const Builders = () => {
  const [allItems, setAllItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectListings, setProjectListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [compareMode, setCompareMode] = useState(false);

  useEffect(() => {
    fetchProperties()
      .then((data) => {
        setAllItems(data);
        setProjects(data.filter((p) => p.content_type === 'project'));
        setLoading(false);
      })
      .catch(() => {
        setLoadError(true);
        setLoading(false);
      });
  }, []);

  const openProject = (project) => {
    setSelectedProject(project);
    setCompareMode(false);
    const projectName = project.project_name || project.title;
    const listings = allItems.filter(
      (p) => p.content_type === 'listing' && (p.project_name === projectName || p.builder === projectName)
    );
    setProjectListings(listings);
  };

  const closeProject = () => { setSelectedProject(null); setProjectListings([]); setCompareMode(false); };

  return (
    <section id="builders" className="py-24 md:py-32 bg-background" data-testid="builders-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-16">
          <p className="text-xs md:text-sm uppercase tracking-widest text-brand-green mb-4" data-testid="builders-subtitle">Explore</p>
          <h2 className="text-4xl md:text-5xl font-heading font-light text-text-primary" data-testid="builders-title">Popular Projects</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="builders-grid">
          {projects.map((project, index) => (
            <motion.div key={project.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} onClick={() => openProject(project)} className="group cursor-pointer" data-testid={`builder-tile-${index}`}>
              <div className="relative overflow-hidden rounded-lg border border-brand-green/10 hover:border-brand-green/40 shadow-sm hover:shadow-xl transition-all duration-500">
                <img src={imageUrl(project.images?.[0])} alt={project.title} loading="lazy" onError={(e) => { e.currentTarget.src = 'https://images.pexels.com/photos/3280078/pexels-photo-3280078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'; }} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-lg md:text-2xl font-heading text-white mb-1">{project.title}</h3>
                  <p className="text-xs text-white/70">{project.location}</p>
                  <div className="flex items-center gap-1 mt-2 text-brand-gold-light text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span>View Listings</span>
                    <ChevronRight size={14} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {!loading && projects.length === 0 && (
          <p className="text-center text-text-muted text-sm" data-testid="builders-empty">
            {loadError
              ? 'Unable to load projects right now. Please try again later.'
              : 'No projects available yet. Please check back soon.'}
          </p>
        )}
      </div>

      <AnimatePresence>
        {selectedProject && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 md:p-8" onClick={closeProject} data-testid="builder-modal">
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 40 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 40 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} onClick={(e) => e.stopPropagation()} className="bg-white border border-brand-green/10 rounded-xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b border-background-tertiary">
                <div>
                  <h2 className="text-3xl font-heading font-light text-text-primary" data-testid="builder-modal-title">{selectedProject.title}</h2>
                  <p className="text-text-secondary text-sm mt-1">Listings under this project</p>
                </div>
                <div className="flex items-center gap-2">
                  {projectListings.length > 1 && (
                    <div className="hidden sm:flex items-center bg-background-secondary rounded-full p-1" data-testid="compare-toggle">
                      <button
                        onClick={() => setCompareMode(false)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!compareMode ? 'bg-brand-green text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                        data-testid="compare-toggle-cards"
                        aria-label="Cards view"
                      >
                        <LayoutGrid size={13} /> Cards
                      </button>
                      <button
                        onClick={() => setCompareMode(true)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${compareMode ? 'bg-brand-green text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                        data-testid="compare-toggle-compare"
                        aria-label="Compare view"
                      >
                        <BarChart3 size={13} /> Compare BHK
                      </button>
                    </div>
                  )}
                  <button onClick={closeProject} className="p-2 text-text-muted hover:text-text-primary transition-colors" data-testid="close-builder-modal"><X size={24} /></button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[65vh]">
                {projectListings.length === 0 ? (
                  <div className="text-center py-12 text-text-secondary">No listings under this project yet.</div>
                ) : compareMode ? (
                  <CompareTable listings={projectListings} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {projectListings.map((listing, idx) => (
                      <motion.div key={listing.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} onClick={() => window.open(`/property/${listing.id}`, '_blank')} className="group cursor-pointer rounded-lg border border-background-tertiary hover:border-brand-green/30 transition-all duration-500 overflow-hidden shadow-sm hover:shadow-lg" data-testid={`builder-project-${idx}`}>
                        <div className="relative h-[180px] overflow-hidden">
                          <img src={imageUrl(listing.images?.[0])} alt={listing.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          <div className="absolute top-3 right-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${listing.status === 'Available' ? 'bg-brand-green/90 text-white' : 'bg-red-500/90 text-white'}`}>{listing.status}</span>
                          </div>
                        </div>
                        <div className="p-4 bg-white">
                          <h3 className="text-lg font-heading text-text-primary mb-1">{listing.title}</h3>
                          {listing.bedrooms && (
                            <p className="text-text-secondary text-xs mb-1">{listing.bedrooms}</p>
                          )}
                          <div className="flex items-center gap-2 text-text-muted text-xs mb-2"><MapPin size={12} /><span>{listing.location}</span></div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xl font-heading text-brand-green">{listing.price || 'Price on request'}</span>
                            <div className="flex items-center gap-1 text-brand-green text-xs"><span>Details</span><ArrowRight size={14} /></div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
